import { Box, MenuItem, Select, TextField } from '@material-ui/core'
import { Button } from 'components/facets'
import { getGradingScale, updateGradingScale } from 'lib/api'
import { useSnackbar } from 'notistack'
import { useRef, useState } from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useTranslation } from 'react-i18next'
import {
  GradingScaleCompany,
  GradingScaleEntry,
  GradingScaleGrade,
  normalizeScaleGrade,
} from 'shared'

const DRAG_TYPE = 'gradingScaleGrade'

// A row the staff has just typed has no id until the save comes back, so the list is keyed on
// something of its own instead of on the id.
interface WorkingGrade {
  key: string
  id: number | null
  grade: string
}

const toWorkingGrade = (grade: GradingScaleGrade): WorkingGrade => ({
  key: String(grade.id),
  id: grade.id,
  grade: grade.grade,
})

// What the list would save, reduced to a string. Comparing it against the one loaded from the server
// is what tells Save whether there is anything to save: dragging a row away and back leaves the same
// signature, so it correctly counts as no change at all.
const listSignature = (rows: WorkingGrade[]): string => rows.map((row) => row.key).join('|')

// The error payload comes in two shapes: the Node error handler answers { error: { message } } while
// some routes answer { error: 'text' }. Handing the object straight to a snackbar takes the whole page
// down with "Objects are not valid as a React child", so it is flattened here. 'unknown' is what the
// handler sends for anything it did not expect, which says nothing to whoever is reading it.
const errorText = (error: any, fallback: string): string => {
  const payload = error?.response?.data?.error

  if (typeof payload === 'string') return payload
  if (typeof payload?.message === 'string' && payload.message !== 'unknown') return payload.message

  return fallback
}

interface GradeRowProps {
  grade: WorkingGrade
  index: number
  moveRow: (from: number, to: number) => void
  onInvalidate: (key: string) => void
}

const GradeRow = ({ grade, index, moveRow, onInvalidate }: GradeRowProps): JSX.Element => {
  const ref = useRef<HTMLLIElement>(null)
  const { t } = useTranslation()

  // The object form on purpose: react-dnd rebuilds the spec on every render, so hover always sees
  // the current index. The memoised factory form would keep the position the row had on mount.
  const [, drop] = useDrop({
    accept: DRAG_TYPE,
    hover: (item: { index: number }) => {
      if (item.index === index) return

      moveRow(item.index, index)
      // The dragged item keeps travelling, so it has to know where it landed or the next hover
      // would move it from a position it no longer occupies.
      item.index = index
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  drag(drop(ref))

  return (
    <li ref={ref} className={`grading-scale-row${isDragging ? ' is-dragging' : ''}`}>
      <i className="fa-light fa-grip-lines grading-scale-handle" />
      <span className="grading-scale-rank">{index + 1}</span>
      <span className="grading-scale-grade">{grade.grade}</span>
      {grade.id === null && <span className="grading-scale-badge">{t('gradingScale.new')}</span>}
      <button
        type="button"
        className="grading-scale-action"
        title={t('gradingScale.invalidate')}
        onClick={() => onInvalidate(grade.key)}
      >
        <i className="fa-light fa-xmark" />
      </button>
    </li>
  )
}

export const GradingScaleManager = (): JSX.Element => {
  const [companies, setCompanies] = useState<GradingScaleCompany[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [ordered, setOrdered] = useState<WorkingGrade[]>([])
  const [invalidated, setInvalidated] = useState<WorkingGrade[]>([])
  // Grades the system assigns to itself, Unknown above all: they stay valid without a rank, so they
  // are neither draggable nor invalidatable and they never travel in the save.
  const [systemGrades, setSystemGrades] = useState<WorkingGrade[]>([])
  const [newGrade, setNewGrade] = useState('')
  // The list as it came back from the server. Save stays disabled until the two differ, so the button
  // turning active is the acknowledgement that a drag or a removal was taken.
  const [baseline, setBaseline] = useState('')
  // Nothing is fetched when the tab opens: this section shares the tab with the other database
  // tools, and whoever came for one of those should not pay for a query they did not ask for.
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const selectedCompany = companies.find((company) => company.id === selectedId)
  const hasChanges = listSignature(ordered) !== baseline

  const loadCompanyGrades = (company: GradingScaleCompany) => {
    const orderedGrades = company.grades
      .filter((grade) => grade.rank !== null)
      .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
      .map(toWorkingGrade)

    setOrdered(orderedGrades)
    setBaseline(listSignature(orderedGrades))
    setInvalidated(
      company.grades.filter((grade) => grade.invalidatedAt !== null).map(toWorkingGrade)
    )
    setSystemGrades(
      company.grades
        .filter((grade) => grade.rank === null && grade.invalidatedAt === null)
        .map(toWorkingGrade)
    )
  }

  const fetchScale = async (keepCompanyId?: number) => {
    setLoading(true)
    try {
      const data = await getGradingScale()
      setCompanies(data)

      const company = data.find((entry) => entry.id === keepCompanyId) ?? data[0]
      if (company) {
        setSelectedId(company.id)
        loadCompanyGrades(company)
      }
    } catch (error: any) {
      enqueueSnackbar(errorText(error, t('gradingScale.loadFailed')), { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const changeCompany = (id: number) => {
    const company = companies.find((entry) => entry.id === id)
    if (!company) return

    setSelectedId(id)
    setNewGrade('')
    loadCompanyGrades(company)
  }

  const moveRow = (from: number, to: number) => {
    setOrdered((current) => {
      const next = [...current]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }

  const invalidateRow = (key: string) => {
    setOrdered((current) => {
      const row = current.find((grade) => grade.key === key)
      // A row that was never saved has nothing to invalidate: dropping it is enough.
      if (row && row.id !== null) setInvalidated((rows) => [...rows, row])

      return current.filter((grade) => grade.key !== key)
    })
  }

  const restoreRow = (key: string) => {
    setInvalidated((current) => {
      const row = current.find((grade) => grade.key === key)
      if (row) setOrdered((rows) => [...rows, row])

      return current.filter((grade) => grade.key !== key)
    })
  }

  const addGrade = () => {
    // Fixed here rather than refused by the backend: what is only a matter of spelling — a missing decimal, a space
    // where the designation wants a hyphen — is corrected and shown back, and only what would need guessing at the
    // grade itself is handed back as a problem.
    const normalized = normalizeScaleGrade(newGrade)

    if ('problem' in normalized) {
      if (normalized.problem !== 'empty') {
        enqueueSnackbar(t(`gradingScale.gradeProblem.${normalized.problem}`), { variant: 'error' })
      }
      return
    }

    const { grade } = normalized

    if (ordered.some((row) => row.grade === grade)) {
      enqueueSnackbar(t('gradingScale.alreadyInList'), { variant: 'error' })
      return
    }

    const alreadyInvalidated = invalidated.find((row) => row.grade === grade)
    if (alreadyInvalidated) {
      restoreRow(alreadyInvalidated.key)
      setNewGrade('')
      return
    }

    setOrdered((current) => [...current, { key: `new-${grade}`, id: null, grade }])
    setNewGrade('')
  }

  const save = async () => {
    if (!selectedCompany) return

    setSaving(true)
    try {
      const entries: GradingScaleEntry[] = ordered.map((row) =>
        row.id !== null ? { id: row.id } : { grade: row.grade }
      )
      const result = await updateGradingScale(
        selectedCompany.id,
        entries,
        selectedCompany.fingerprint
      )

      const { created, invalidated: invalidatedGrades, reranked } = result.diff
      enqueueSnackbar(
        t('gradingScale.saveSuccess', {
          created: created.length,
          invalidated: invalidatedGrades.length,
          reranked: reranked.length,
        }),
        { variant: 'success' }
      )

      await fetchScale(selectedCompany.id)
    } catch (error: any) {
      enqueueSnackbar(errorText(error, t('gradingScale.saveFailed')), { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  if (!selectedCompany)
    return (
      <div className="grading-scale">
        <h3>{t('gradingScale.title')}</h3>
        <p className="grading-scale-hint">{t('gradingScale.hint')}</p>
        <Button onClick={() => fetchScale()} disabled={loading} variant="outlined">
          {loading ? t('common.loading') : t('gradingScale.load')}
        </Button>
      </div>
    )

  return (
    <div className="grading-scale">
      <h3>{t('gradingScale.title')}</h3>
      <p className="grading-scale-hint">{t('gradingScale.hint')}</p>

      <div className="d-flex align-items-center gap-2 mb-3">
        <label htmlFor="grading-scale-company">{t('gradingScale.company')}</label>
        <Select
          id="grading-scale-company"
          value={selectedId ?? ''}
          onChange={(event) => changeCompany(Number(event.target.value))}
        >
          {companies.map((company) => (
            <MenuItem key={company.id} value={company.id}>
              {company.code} - {company.name}
            </MenuItem>
          ))}
        </Select>
      </div>

      <section className="grading-scale-column">
        <h4>{t('gradingScale.selectableTitle')}</h4>
        <DndProvider backend={HTML5Backend}>
          <ul className="grading-scale-list">
            {ordered.map((grade, index) => (
              <GradeRow
                key={grade.key}
                grade={grade}
                index={index}
                moveRow={moveRow}
                onInvalidate={invalidateRow}
              />
            ))}
            {/* Sits in the list so it is visible, inert so it cannot be dragged, invalidated or
                sent in the save: a rank would put it in front of the sellers. */}
            {systemGrades.map((grade) => (
              <li
                key={grade.key}
                className="grading-scale-row is-disabled"
                title={t('gradingScale.systemHint')}
              >
                <i className="fa-light fa-lock grading-scale-handle" />
                <span className="grading-scale-rank">-</span>
                <span className="grading-scale-grade">{grade.grade}</span>
              </li>
            ))}
          </ul>
        </DndProvider>

        <div className="d-flex align-items-center gap-2 mt-2">
          <Box width={250}>
            <TextField
              type="text"
              fullWidth
              value={newGrade}
              placeholder={t('gradingScale.newGradePlaceholder')}
              onChange={(event) => setNewGrade(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && addGrade()}
            />
          </Box>
          <Button variant="outlined" onClick={addGrade}>
            {t('gradingScale.add')}
          </Button>
        </div>
      </section>

      <div className="mt-4">
        <Button onClick={save} disabled={saving || !hasChanges} variant="contained">
          {saving ? t('common.loading') : t('common.save')}
        </Button>
      </div>
    </div>
  )
}
